import React, { useEffect, useState } from "react";
import { Slack, ShoppingBag, Phone, Calendar, CreditCard, LifeBuoy, MessageSquare, Cloud, Salad } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const CATALOG = [
    { key: "slack", title: "Slack", description: "Send leads, escalations and daily digests to a Slack channel.", icon: Slack },
    { key: "shopify", title: "Shopify", description: "Connect a Shopify store so Priya can answer order questions.", icon: ShoppingBag },
    { key: "twilio", title: "Twilio", description: "Enable inbound phone calls handled by Priya.", icon: Phone, locked: true },
    { key: "calendly", title: "Calendly", description: "Let Priya book Calendly events for guests in chat.", icon: Calendar },
    { key: "stripe", title: "Stripe", description: "Manage payments, billing and refunds directly from chat.", icon: CreditCard },
    { key: "zendesk", title: "Zendesk", description: "Escalate tickets, draft suggestions and auto-reply.", icon: LifeBuoy },
    { key: "intercom", title: "Intercom", description: "Sync Priya conversations with Intercom inboxes.", icon: MessageSquare },
    { key: "salesforce", title: "Salesforce", description: "Push leads and contacts into your Salesforce org.", icon: Cloud },
    { key: "sunshine", title: "Sunshine", description: "Connect Sunshine for omni-channel customer profiles.", icon: Salad },
];

export default function SettingsIntegrations() {
    const [connected, setConnected] = useState({});

    useEffect(() => {
        adminApi.get("/api/admin/integrations").then((r) => setConnected(r.data.integrations || {})).catch(() => {});
    }, []);

    const toggle = async (key) => {
        const next = { ...connected, [key]: !connected[key] };
        setConnected(next);
        try {
            await adminApi.put("/api/admin/integrations", { integrations: next });
            toast.success(next[key] ? `${key} connected` : `${key} disconnected`);
        } catch (e) {
            setConnected(connected);
            toast.error("Couldn't update");
        }
    };

    return (
        <div className="p-10 max-w-6xl">
            <PageHeader eyebrow="Settings" title="Integrations" description="Connect external services Priya can hand off to or pull data from." testid="page-settings-integrations" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {CATALOG.map((i) => {
                    const isConnected = !!connected[i.key];
                    return (
                        <div key={i.key} className="bg-white border border-stone-200 rounded-xl p-5" data-testid={`integration-${i.key}`}>
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-stone-100 text-stone-700 flex items-center justify-center shrink-0">
                                    <i.icon size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-stone-900 text-sm">{i.title}</div>
                                </div>
                                {isConnected && (
                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sage-500/15 text-sage-700 border border-sage-500/30">
                                        Connected
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-stone-600 mb-4 leading-relaxed">{i.description}</p>
                            <button
                                onClick={() => !i.locked && toggle(i.key)}
                                disabled={i.locked}
                                data-testid={`integration-${i.key}-toggle`}
                                className={`w-full text-xs font-medium px-3 py-2 rounded-md transition-colors ${
                                    i.locked
                                        ? "bg-stone-100 text-stone-400 cursor-not-allowed"
                                        : isConnected
                                          ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                                          : "bg-orchid-700 hover:bg-orchid-600 text-white"
                                }`}
                            >
                                {i.locked ? "Upgrade to enable" : isConnected ? "Disconnect" : "Connect"}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
